import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Command,
  CommandEmpty,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui";
import { GetProductsForInstallDocument, type GetProductsForInstallQuery } from "~/features/admin/tenants/gqls";
import type { TenantProductConnection } from "~/features/admin/tenants/types";

export interface InstallProductOption {
  id: string;
  name: string;
}

interface InstallProductDialogProps {
  open: boolean;
  onClose: () => void;
  /** Already-installed connections; their products are not offered again. */
  connections: TenantProductConnection[];
  onSelect: (product: InstallProductOption) => void;
}

/**
 * Pick a product to install from the active products the tenant has not
 * installed yet. The actual install is confirmed by the caller (AlertDialog)
 * after selection; the connection starts as private.
 */
export function InstallProductDialog({ open, onClose, connections, onSelect }: InstallProductDialogProps) {
  const { t } = useTranslation();
  const { data, loading } = useQuery<GetProductsForInstallQuery>(GetProductsForInstallDocument, { skip: !open });

  const options = useMemo(() => {
    const installed = new Set(connections.map((c) => c.productId));
    return (data?.products ?? []).filter((p) => !installed.has(p.id));
  }, [data, connections]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">{t("feature.tenants.installProduct")}</DialogTitle>
        </DialogHeader>

        <Command shouldFilter={false} className="rounded-md border border-border-default">
          <CommandList>
            {!loading && <CommandEmpty>{t("feature.tenants.noProductsToInstall")}</CommandEmpty>}
            {options.map((option) => (
              <CommandItem key={option.id} value={option.id} onSelect={() => onSelect(option)}>
                <span className="truncate text-fg-1">{option.name}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
